import React, { useState } from "react";
import { ArrowLeftIcon, ClockIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ShippingReportForm from "../../components/forms/shippingReports/ShippingReportForm";
import ShippingReportImageSection from "../../components/forms/shippingReports/ShippingReportImageSection";
import ArrivalTemperatureReadings from "../../components/forms/shippingReports/ArrivalTemperatureReadings";
import ShippingReportAuditLog from "../../components/shippingReports/ShippingReportAuditLog";
import SlideOver from "../../components/slideOver/SlideOver";
import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { useShippingReportViewModel } from "../../viewmodels/shippingReports/useShippingReportViewModel";

const primaryButton =
  "inline-flex items-center justify-center rounded-md bg-main-purple px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton =
  "inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

function ShippingReportDetails(props: any) {
  const { logout, addSnackbar } = props;
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [auditPanel, setAuditPanel] = useState({
    open: false,
    childComponent: null as React.ReactNode,
    customClass: "max-w-2xl",
  });
  const {
    report,
    values,
    loading,
    saving,
    savingTemperature,
    carrierOptions,
    uploadingType,
    error,
    dirty,
    imagesByType,
    changeValue,
    save,
    complete,
    reopen,
    uploadImages,
    deleteImage,
    updateImageCaption,
    moveImage,
    downloadPdf,
    addArrivalTemperature,
    deleteArrivalTemperature,
    loadReport,
  } = useShippingReportViewModel({ reportId, logout, addSnackbar });

  const backPath = report?.shipping_id
    ? `/shippings-v2/${report.shipping_id}`
    : report?.sun_order_id
      ? `/sun-orders/${report.sun_order_id}`
      : "/shippings";

  const goBack = () => {
    if (dirty && !window.confirm("Leave without saving your report changes?")) {
      return;
    }
    navigate(backPath);
  };

  if (loading) {
    return <div className="px-4 py-10 text-sm text-slate-500">Loading shipment report...</div>;
  }

  if (!report) {
    return (
      <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 p-5">
        <h1 className="font-semibold text-rose-900">Shipment report unavailable</h1>
        <p className="mt-1 text-sm text-rose-700">{error}</p>
        <div className="mt-4 flex gap-2">
          <button type="button" className={secondaryButton} onClick={goBack}>Back</button>
          <button type="button" className={primaryButton} onClick={loadReport}>Try Again</button>
        </div>
      </div>
    );
  }

  const completed = report.status === "completed";
  const parentLabel = report.shipping_id
    ? `Shipment #${report.shipping_id}`
      : `Sun Order #${report.sun_order_id}`;

  const openAuditHistory = () => {
    const closePanel = () =>
      setAuditPanel({
        open: false,
        childComponent: null,
        customClass: "max-w-2xl",
      });
    setAuditPanel({
      open: true,
      customClass: "max-w-2xl",
      childComponent: (
        <ShippingReportAuditLog
          audits={report.audits ?? []}
          onClose={closePanel}
        />
      ),
    });
  };

  return (
    <div className="px-3 py-4 sm:px-6 sm:py-6">
      <div className="mx-auto max-w-7xl">
        <button type="button" onClick={goBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ArrowLeftIcon className="h-4 w-4" /> Back to {parentLabel}
        </button>

        <div className="mb-5 flex flex-col justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold text-slate-900">Shipment Loading Report</h1>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${completed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {report.status}
              </span>
              {dirty ? <span className="text-xs font-medium text-amber-700">Unsaved changes</span> : null}
            </div>
            <p className="mt-1 text-sm text-slate-500">{parentLabel} · Report #{report.id} · Times shown in America/Toronto</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className={secondaryButton} onClick={openAuditHistory}>
              <ClockIcon className="mr-2 h-5 w-5" /> Audit History
            </button>
            <button type="button" className={secondaryButton} disabled={saving} onClick={downloadPdf}>
              <DocumentArrowDownIcon className="mr-2 h-5 w-5" /> Download PDF
            </button>
            {completed ? (
              <button type="button" className={primaryButton} disabled={saving} onClick={reopen}>Reopen Report</button>
            ) : (
              <>
                <button type="button" className={secondaryButton} disabled={saving || !dirty} onClick={() => save()}>
                  {saving ? "Saving..." : "Save Draft"}
                </button>
                <button type="button" className={primaryButton} disabled={saving} onClick={complete}>Mark Completed</button>
              </>
            )}
          </div>
        </div>

        {error ? <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

        {completed ? (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            This report is read-only. Reopen it to change fields or pictures.
          </div>
        ) : null}

        <ShippingReportForm values={values} onChange={changeValue} disabled={completed || saving} carrierOptions={carrierOptions} />

        <div className="mt-5 space-y-5">
          <ArrivalTemperatureReadings
            readings={report.arrival_temperature_readings ?? []}
            disabled={completed}
            saving={savingTemperature}
            onAdd={addArrivalTemperature}
            onDelete={deleteArrivalTemperature}
          />
          <ShippingReportImageSection
            title="Bill of Lading Pictures"
            description="Take or upload one or more clear pictures of the BOL."
            type="bol"
            images={imagesByType.bol}
            uploading={uploadingType === "bol"}
            disabled={completed}
            onUpload={uploadImages}
            onDelete={deleteImage}
            onCaptionChange={updateImageCaption}
            onMove={moveImage}
          />
          <ShippingReportImageSection
            title="Truck Temperature Monitor Picture"
            description="Required before completion. Take a clear picture showing the truck temperature monitor display."
            type="temp_monitor"
            images={imagesByType.tempMonitor}
            uploading={uploadingType === "temp_monitor"}
            disabled={completed}
            onUpload={uploadImages}
            onDelete={deleteImage}
            onCaptionChange={updateImageCaption}
            onMove={moveImage}
          />
          <ShippingReportImageSection
            title="Pallet Pictures"
            description="These pictures appear in order at the end of the generated PDF."
            type="pallet"
            images={imagesByType.pallet}
            uploading={uploadingType === "pallet"}
            disabled={completed}
            onUpload={uploadImages}
            onDelete={deleteImage}
            onCaptionChange={updateImageCaption}
            onMove={moveImage}
          />
        </div>

        {!completed ? (
          <div className="sticky bottom-3 mt-5 flex justify-end gap-2 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
            <button type="button" className={secondaryButton} disabled={saving || !dirty} onClick={() => save()}>
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button type="button" className={primaryButton} disabled={saving} onClick={complete}>Mark Completed</button>
          </div>
        ) : null}
      </div>
      <SlideOver
        childComponent={auditPanel.childComponent}
        slideOver={auditPanel}
        customClass={auditPanel.customClass}
        setSlideOver={setAuditPanel}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar: any) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(connect(null, mapDispatchToProps)(ShippingReportDetails));
